import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";
import { extractErrorMessage } from "utils/errorHandler";

export interface LeaveRequest {
  _id: string;
  leaveType: string;
  reason: string;
  fromDate: string;
  toDate: string;
  isCertified: boolean;
  certificateDocument: string;
  unpaidDays: number;
  departmentId: string;
  status?: string;
  org?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type LeaveRequestPayload = {
  leaveType: string;
  reason: string;
  fromDate: string;
  toDate: string;
  isCertified: boolean;
  certificateDocument: string;
  unpaidDays: number;
  departmentId: string;
};

export type LeaveRequestResponse = {
  success: boolean;
  message: string;
  data: LeaveRequest;
};

const addLeaveRequest = async (
  payload: LeaveRequestPayload
): Promise<LeaveRequestResponse> => {
  const { data } = await customAxios.post<LeaveRequestResponse>(
    "/leaves/request",
    payload
  );
  return data;
};

export const useAddLeaveRequest = () => {
  const queryClient = useQueryClient();

  return useMutation<LeaveRequestResponse, AxiosError, LeaveRequestPayload>({
    mutationFn: addLeaveRequest,
    onSuccess: async (data) => {
      showNotification({
        title: "Success",
        message: data.message,
        color: "green",
      });

      await queryClient.invalidateQueries(["leaves"]);
    },
    onError: (error) => {
      showNotification({
        title: "Leave Request Failed",
        message: extractErrorMessage(error, "Failed to add leave request"),
        color: "red",
        autoClose: 8000, // Show for 8 seconds for longer messages
      });
    },
  });
};
